import { faSpiderWeb } from "@fortawesome/pro-light-svg-icons/faSpiderWeb";
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, CardContent, Grid, InputAdornment, Pagination, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from 'app/appConstants';
import { CorePermissions } from "app/appPermissions";
import { MprcBarTitle, MprcCard, MprcCardActions, MprcContainer, MprcIconButton, MprcIconFa, MprcLink, MprcRequestErrorDialog, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from 'components';
import { MprcFilterBoxContent, MprcFilterCount } from 'components/filter';
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconButtonDelete } from 'components/iconButtons';
import { IconAdd, IconChevronExpand, IconSort } from 'components/icons';
import { AutoCompleteDataModel, AutoCompleteSuggestions } from "components/MprcAutoComplete";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcDropDownItemAny, MprcDropdownItems, MprcIconButtonDropdown } from 'components/MprcIconButtonDropdown';
import { MprcLinkDropdown } from 'components/MprcLinkDropdown';
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { ChannelPermissionsResponse, ChannelRoles, ChannelRolesSelectorResponse, ManagedChannelMembersListOrderBy, ManagedChannelMembersListResponse, ManagedChannelMembersListResponsePaginatedList, MemberListDtoPaginatedList, MemberType, UnitTypeResponse } from 'generated/CoreApi_v1';
import { TFunction } from "i18next";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router";
import { NIL } from "uuid";
import { ListMemberDrawer } from 'views/foundation/member/drawers/ListMemberDrawer';
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";
import { UserProfileDialog } from "views/foundation/user/dialogs/UserProfileDialog";
import { useChannelMemberOperations } from 'views/knowledge/channels/Home/EditChannel/useChannelMemberOperations';
import EditChannelAddMembers, { MembersSource } from './EditChannelAddMembers';
import { EditChannelHeader } from './EditChannelHeader';

////////////////////////////////////////////////////////////////////////////

export interface AddValueOption {
    name?: string;
    option: MembersSource;
    id?: string;
}

interface IViewDataFilter {
    searchText: string;
    orderBy: ManagedChannelMembersListOrderBy;
    expandedMode: boolean;
}

////////////////////////////////////////////////////////////////////////////

export const EditChannelListMembers: React.FC = () => {

    const api = useCoreApi();
    const session = useSession();
    const navigate = useNavigate();
    const { channelId: id } = useParams();
    const { t } = useTranslation("knowledge");

    const { addMembers, deleteMember, changeMemberRole, loading: saving, error: memberOperationError, clearError } = useChannelMemberOperations();

    const [filterMode, setFilterMode] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewUnitTypes, setViewUnitTypes] = useState<UnitTypeResponse[]>([]);
    const [viewChannelRoles, setViewChannelRoles] = useState<ChannelRolesSelectorResponse[]>([]);
    const [viewData, setViewData] = useState<ManagedChannelMembersListResponsePaginatedList>();
    const [permission, setPermission] = useState<ChannelPermissionsResponse>();
    const [viewUser, setViewUser] = useState<string | null>(null);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        expandedMode: false,
        orderBy: ManagedChannelMembersListOrderBy.Role,
        searchText: ""
    });
    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);

    const pageNumber = useRef<number>(1);
    const channelId = id ?? NIL;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    function getAddMenuItems(t: TFunction): MprcDropdownItem<AddValueOption>[] {
        const allGroups: MprcDropdownItem<AddValueOption>[][] = [];

        //
        const group0: MprcDropdownItems<AddValueOption> = [
            { text: t("Channel.Edit.Members.AddUserAction"), value: { option: "user" } }
        ];

        //
        const group1: MprcDropdownItems<AddValueOption> = [];
        viewUnitTypes.forEach((item, i) => group1.push(
            {
                text: item.name + "...",
                value: { option: "unit", id: item.unitTypeId, name: item.name }
            }
        ));

        //
        const group2: MprcDropdownItems<AddValueOption> = [
            { text: t("Channel.Edit.Members.AddGroupAction"), value: { option: "group" } }
        ];
        if (session.permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            group2.push({ text: t("Channel.Edit.Members.AddChannelAction"), value: { option: "channel" } });
            group2.push({ text: t("Channel.Edit.Members.AddTopicAction"), value: { option: "topic" } });
            group2.push({ text: t("Channel.Edit.Members.AddTrainingMembersAction"), value: { option: "training" } });
        }

        //
        const group3: MprcDropdownItems<AddValueOption> = [
            { text: t("Channel.Edit.Members.AddSubordinatesAction"), value: { option: "subordinates" } }
        ];

        //
        allGroups.push(group0, group1, group2, group3);

        //
        if (session.permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            const group4: MprcDropdownItems<AddValueOption> = [
                { text: t("Channel.Edit.Members.AddAllUsersAction"), value: { option: "allUsers" } },
                { text: t("Channel.Edit.Members.AddExternalUsersAction"), value: { option: "externalUsers" } },
                { text: t("Channel.Edit.Members.AddInternalUsersAction"), value: { option: "internalUsers" } }
            ];
            allGroups.push(group4);
        }

        return MprcDropdownBuildGroupedItems<AddValueOption>(allGroups);
    }

    ///
    const onAddMenuChange = (option: AddValueOption) => {
        setDialog(
            <EditChannelAddMembers
                option={option}
                channelId={channelId}
                onClose={(success) => {
                    if (success) setLoading(true);
                    setDialog(null);
                }}
                channelRoles={viewChannelRoles}
            />
        );
    };

    ///
    const loadPage = (pageNum: number) => {
        pageNumber.current = pageNum;
        setLoading(true);
    };

    ///
    const expandResults = () => {
        setViewFilter({
            ...viewFilter, expandedMode: !viewFilter.expandedMode
        });
        loadPage(1);
    };

    ///
    const resetFilters = () => {
        searchTextField.current?.reset();
        setLoading(true);
    };

    ///
    const handleChangeMemberRole = async (memberId: string, memberType: MemberType, currentRole: ChannelRoles, targetRole: ChannelRoles) => {
        if (currentRole === targetRole) return;

        const success = await changeMemberRole({ channelId, memberId, memberType, role: targetRole });

        if (success) {
            setLoading(true);
        }
    };

    ///
    const showGroupMembers = (groupId: string, groupName: string) => {
        setDialog(
            <ListMemberDrawer
                subtitle={groupName}
                onClose={() => setDialog(undefined)}
                onGetData={({ searchText, pageNumber, pageSize, expanded }, signal) =>
                    new Promise<MemberListDtoPaginatedList>((resolve, reject) => {
                        api.v1.groupManagement.managedGroupGetCurrentMembers({
                            groupId: groupId,
                            searchText: searchText,
                            expanded: expanded,
                            pageNumber,
                            pageSize
                        }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ///
    const handleDelete = async (item: ManagedChannelMembersListResponse) => {
        const success = await deleteMember({ channelId, memberType: item.memberType, memberId: item.memberId });

        if (success) {
            if (viewData?.items.length == 1 && pageNumber.current > 1) {
                pageNumber.current--;
            }
            setLoading(true);
        }
    };

    ///
    const handleAddMember = async (item: AutoCompleteDataModel) => {
        const success = await addMembers({
            channelId: channelId,
            members: [item.key],
            role: ChannelRoles.Contributor,
            memberType: MemberType.User
        });

        if (success) {
            pageNumber.current = 1;
            setLoading(true);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterBoxContent
                title={permission && permission.canAddMembers === true && <MprcIconButtonDropdown
                    icon={<IconAdd />}
                    title={t("Channel.Edit.Members.AddMemberButtonTooltip")}
                    text={t("Channel.Edit.Members.AddMemberButtonText")}
                    color="muted"
                    separator="blank"
                    onChange={(selection) => onAddMenuChange(selection)}
                    items={getAddMenuItems(t)}
                />}
                filterAction={{
                    readonly: false,
                    filtered: filterMode,
                    onClick: (e) => setFilterMode(!filterMode)
                }}
                actions={
                    [
                        <MprcIconButton title={t("Channel.Edit.Members.ExpandGroupsTooltip")} onClick={expandResults} color={viewFilter.expandedMode ? "warning" : "muted"}>
                            <MprcIconFa icon={faSpiderWeb} size="sm" />
                        </MprcIconButton>
                        ,
                        <MprcIconButtonDropdown
                            menuItemCheck
                            icon={<IconSort />}
                            selected={viewFilter.orderBy}
                            onChange={(orderBy) => {
                                setViewFilter({ ...viewFilter, orderBy });
                                loadPage(1);
                            }}
                            items={[
                                { text: t("Channel.Edit.Members.NameText"), value: ManagedChannelMembersListOrderBy.Name },
                                { text: t("Channel.Edit.Members.PermissionLevelText"), value: ManagedChannelMembersListOrderBy.Role },
                            ]}
                            title={t("Channel.List.OrderByButtonTooltip")}
                        />
                    ]
                }
            >
                {filterMode ?
                    <MprcSearchTextField
                        autoFocus
                        initialValue={viewFilter.searchText}
                        placeholder={t("Channel.Edit.Members.SearchTextPlaceholder")}
                        ref={searchTextField}
                        onConfirm={(value) => {
                            setViewFilter({ ...viewFilter, searchText: value });
                            loadPage(1);
                        }}
                    />
                    : <UserAutoComplete
                        startAdornment={
                            <InputAdornment position="start">
                                <MprcText color={["muted", "main"]}>
                                    <FontAwesomeIcon icon={faUserPlus} />
                                </MprcText>
                            </InputAdornment>
                        }
                        selectedItems={[]}
                        onChange={(items) => items.length > 0 ?
                            handleAddMember(items[0]) : undefined}
                        onError={e => MprcRequestSnackbarErrorReport(e)}
                        placeholder={t("Channel.Edit.Members.AddContributorPlaceholder")}
                        onGetDataCustom={({ searchText, pageNumber, pageSize }, signal) =>
                            new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve, reject) => {
                                api.v1.knowledgeSuggestions.channelMemberUsers({
                                    channelId,
                                    searchText: searchText ?? null,
                                    pageNumber,
                                    pageSize
                                }, { signal })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            })
                        }
                    />}

            </MprcFilterBoxContent>
        </MprcCardFilterBox>
    );

    ///
    const RenderGrid =
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            {viewData?.items.map(item => (
                <Grid key={item.memberId} width="100%">
                    <Grid container justifyContent="center" alignItems="center" spacing={2} width="100%">
                        <Grid alignSelf="flex-start">
                            <MprcLink onClick={() => item.memberType == MemberType.User ? setViewUser(item.memberId) : showGroupMembers(item.memberId, item.name)}>
                                <Avatar
                                    src={item.thumbnailUrl}
                                    title={item.name}
                                    sx={(theme) => (
                                        { width: 32, height: 32, backgroundColor: theme.palette.grey[200] }
                                    )}
                                />
                            </MprcLink>
                        </Grid>
                        <Grid size="grow" overflow="hidden">
                            <Grid container direction="column" flexWrap="nowrap" width="100%">
                                <Grid>
                                    <MprcListItemTitle onClick={item.memberType == MemberType.User ? () => setViewUser(item.memberId) : () => showGroupMembers(item.memberId, item.name)}>
                                        {item.name}
                                    </MprcListItemTitle>
                                </Grid>
                                <Grid sx={{ mt: -0.5 }}>
                                    <Typography variant="caption" color={viewFilter.expandedMode === true ? "muted.main" : "primary.light"}>
                                        <MprcLinkDropdown
                                            text={item.roleName}
                                            items={viewChannelRoles.map(x => ({ text: x.name, value: x.id } as MprcDropDownItemAny))}
                                            checkSelectedItem
                                            selectedItem={item.role}
                                            disabled={viewFilter.expandedMode === true}
                                            onChange={(selectedValue) => handleChangeMemberRole(item.memberId, item.memberType, item.role, selectedValue)}
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid>
                            {item.role != ChannelRoles.Owner && <IconButtonDelete iconSize="sm" size="small" color="muted" onClick={() => handleDelete(item)} />}
                        </Grid>
                    </Grid>
                </Grid>
            ))}
        </Grid >;

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT
    ////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {

        async function getData() {
            try {

                const promises: any[] = [
                    api.v1.channelManagement.getMembers({
                        ChannelId: channelId,
                        ExpandedMode: viewFilter.expandedMode,
                        SearchText: viewFilter.searchText,
                        OrderBy: viewFilter.orderBy,
                        PageNumber: pageNumber.current,
                        PageSize: APP_PAGE_SIZE_DEFAULT
                    }),
                ];

                if (!permission)
                    promises.push(api.v1.channelManagement.getChannelPermissions({ ChannelId: channelId }));

                if (viewUnitTypes.length == 0)
                    promises.push(api.v1.unitType.getUnitTypes());

                if (viewChannelRoles.length == 0)
                    promises.push(api.v1.knowledgeSelectors.channelRoles({}));

                await Promise.all(promises)
                    .then(results => {
                        const result = results[0];
                        setViewData(result.data as ManagedChannelMembersListResponsePaginatedList);

                        if (results.hasOwnProperty(1)) {
                            const resultPermission = results[1];
                            setPermission(resultPermission.data as ChannelPermissionsResponse);
                        }

                        if (results.hasOwnProperty(2)) {
                            const resultUnitTypes = results[2];
                            setViewUnitTypes(resultUnitTypes.data as UnitTypeResponse[]);
                        }

                        if (results.hasOwnProperty(3)) {
                            const resultChannelRoles = results[3];
                            setViewChannelRoles(resultChannelRoles.data as ChannelRolesSelectorResponse[]);
                        }
                    });

            } catch (error) {
                // Error handling is done by the hook
                console.error('Error loading data:', error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <EditChannelHeader
                onClose={() => navigate(-1)}
                recordChanged={false}
                reloadOnClose={false}
                actions={[<MprcBarTitle>{permission?.channelName ?? ""}</MprcBarTitle>]}
            />

            {dialog}

            <MprcRequestIndicator
                visible={saving}
                type={"post"}
            />

            <MprcRequestErrorDialog
                error={memberOperationError}
                onClose={clearError}
            />

            <MprcContainer>
                <MprcCard>
                    {RenderFilter}
                    <CardContent>
                        {loading
                            ? <MprcSpinnerIndicator />
                            : viewData && (
                                <MprcContainer>
                                    <MprcContainer>
                                        <MprcFilterCount
                                            loading={loading}
                                            isEmpty={viewData.totalRows == 0}
                                            countText={t("Channel.Edit.Members.RecordsFoundText", { count: viewData.totalRows })}
                                            filter={{
                                                active: viewFilter.searchText.length > 0,
                                                text: t("Channel.Edit.Members.RecordsFilteredText", { count: viewData.totalRows }),
                                                onResetFilter: resetFilters
                                            }}
                                        />
                                    </MprcContainer>

                                    {RenderGrid}
                                </MprcContainer>
                            )
                        }
                    </CardContent>

                    <MprcCardActions>
                        {!loading && viewData && viewData.totalPages > 1 && (
                            <Grid alignSelf="center">
                                <Pagination
                                    shape="rounded"
                                    color="primary"
                                    page={viewData.pageNumber}
                                    count={viewData.totalPages}
                                    onChange={(e, pageNumber) => {
                                        if (pageNumber != viewData.pageNumber) {
                                            loadPage(pageNumber);
                                        }
                                    }}
                                />
                            </Grid>
                        )}
                    </MprcCardActions>
                </MprcCard>
            </MprcContainer>
            {
                !loading && (
                    <Accordion disableGutters sx={(theme) => ({ bgcolor: theme.palette.notice.dark })} defaultExpanded={true}>
                        <AccordionSummary expandIcon={<IconChevronExpand />}>
                            <Typography variant="h3">{t("Channel.Edit.Members.InfoCardTitle")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="caption" color="muted.main">
                                <ul style={{ marginTop: 0 }}>
                                    <li><Trans i18nKey={t("Channel.Edit.Members.InfoCardL1")} /></li>
                                    <li><Trans i18nKey={t("Channel.Edit.Members.InfoCardL2")} /></li>
                                    <li><Trans i18nKey={t("Channel.Edit.Members.InfoCardL3")} /></li>
                                    <li><Trans i18nKey={t("Channel.Edit.Members.InfoCardL4")} /></li>
                                    <li><Trans i18nKey={t("Channel.Edit.Members.InfoCardL5")} /></li>
                                </ul>
                                <Trans i18nKey={t("Channel.Edit.Members.InfoCardL6")} />
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                )
            }

            <UserProfileDialog
                userId={viewUser}
                onClose={() => setViewUser(null)}
            />
        </>
    );
};
